import React, { useEffect, useState } from 'react'
import { View, Text as RNText, TextStyle, ViewStyle } from 'react-native'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'
import { Button as MuiButton } from '@mui/material'
import { ChevronLeftRounded, LinkRounded } from '@mui/icons-material'
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'
import { TextField, InputAdornment } from '@mui/material'
import { useStores } from '@trivie/core'
import { Button, color } from '@trivie/ui-mobile'
import { Text } from '@trivie/ui-web/components/text'
import { FullScreenLoader } from '@trivie/ui-web/components/full-screen-loader'

export const SSOEntryScreen = () => {
  const { userStore } = useStores()
  const { urlVerify, getAccessCodeDetails, ssoWorkspace } = userStore

  const { accessCode } = useParams() as any
  const history = useHistory()

  const [workspaceID, setWorkspaceID] = useState(accessCode ?? '')
  const [notFound, setNotFound] = useState(false)

  const [url, setUrl] = useState('')

  const verify = async () => {
    urlVerify(url).then(async (data) => {
      if (data.access_code) {
        const redirect = await getAccessCodeDetails(data.access_code)
        if (redirect) {
          window.location.replace(redirect)
        } else {
          setNotFound(true)
        }
      } else {
        setNotFound(true)
      }
    })
  }

  useEffect(() => {
    if (workspaceID) {
      getCodeDetails(workspaceID)
    } else if (ssoWorkspace) {
      getCodeDetails(ssoWorkspace.access_code)
      userStore.setSSOWorkspace(undefined)
    }
  }, [])

  const getCodeDetails = async (access: string) => {
    const redirect = await getAccessCodeDetails(access)
    if (redirect) {
      localStorage.setItem('redirecting-sso', 'true')
      window.location.replace(redirect)
    } else {
      setNotFound(true)
      setWorkspaceID('')
    }
  }

  return accessCode ? (
    <FullScreenLoader />
  ) : (
    <div style={ROOT}>
      <div style={CONTAINER}>
        <div style={CONTENT}>
          <div style={HEADER}>
            <Text style={HEADLINE} text="Log in with SSO" variant="headline" />
            <Text variant="titleMedium" style={TAGLINE} text="Enter your company name to log in" />
            <TextField
              variant="standard"
              onChange={(e) => {
                setNotFound(false)
                setUrl(e.target.value)
              }}
              value={url}
              InputProps={{
                style: { paddingBottom: 8 },
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkRounded htmlColor={color.black} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Text text=".trivie.com" variant="title" />
                  </InputAdornment>
                ),
              }}
              placeholder={url ? '' : 'your-company'}
            />
            {notFound && (
              <div style={ERROR_STATE}>
                <SentimentVeryDissatisfiedIcon
                  htmlColor={color.red}
                  style={{ marginRight: 12, fontSize: 20 }}
                />
                <RNText style={{ fontWeight: '700', fontSize: 12, color: 'rgba(255, 0, 0, 0.8)' }}>
                  We couldn't find your workspace.
                  <Text
                    style={{ fontSize: 12, color: 'rgba(255, 0, 0, 0.8)' }}
                    variant="body2Medium"
                    tx="Onboarding.Login.tryAgain"
                  />
                </RNText>
              </div>
            )}
            <Button
              accessibilityRole="link"
              accessibilityLabel="Continue"
              disabled={url.length === 0}
              style={SUBMIT}
              tx="Common.continue"
              onPress={verify}
            />
            <span style={{ width: 400, textAlign: 'center' }}>
              <Text
                component="span"
                text="If you don't know your company name, try "
                variant="body1"
                style={NEED_HELP}
              />
              <Text
                component="span"
                text="logging in with your email address or phone number,"
                variant="body1"
                onClick={() => history.replace('/login/magic')}
                style={{ ...NEED_HELP, cursor: 'pointer', textDecoration: 'underline' }}
              />
              <Text
                component="span"
                variant="body1"
                style={NEED_HELP}
                text=" or contact your training administrator for help."
              />
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'row' }}>
            <MuiButton
              onClick={() => history.replace('/login/magic')}
              style={{ textTransform: 'none' }}
              startIcon={<ChevronLeftRounded style={{ color: color.shade70, fontSize: 24 }} />}
            >
              Back
            </MuiButton>
          </div>
        </div>
      </div>
    </div>
  )
}

const ROOT: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
}
const CONTAINER: React.CSSProperties = {
  flexDirection: 'column',
  flex: 1,
  display: 'flex',
  maxHeight: 576,
  justifyContent: 'center',
  width: '100%',
  alignSelf: 'center',
}
const CONTENT: React.CSSProperties = {
  height: '100%',
  flex: 1,
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignSelf: 'center',
}
const HEADER: React.CSSProperties = {
  minWidth: 319,
  flexDirection: 'column',
  display: 'flex',
  marginBottom: 20,
}
const SUBMIT: ViewStyle = {
  marginHorizontal: 20,
  alignSelf: 'center',
  width: '100%',
  marginTop: 20,
  marginBottom: 60,
}
const TAGLINE: React.CSSProperties = {
  opacity: 0.5,
  textAlign: 'center',
  marginBottom: 50,
}
const HEADLINE: React.CSSProperties = { textAlign: 'center', marginBottom: 12 }
const NEED_HELP: React.CSSProperties = {
  textAlign: 'center',
  alignSelf: 'center',
  width: 330,
  color: color.shade50,
  fontSize: 14,
}
const ERROR_STATE: React.CSSProperties = {
  width: 400,
  height: 64,
  marginTop: 32,
  alignSelf: 'center',
  marginBottom: 25,
  borderRadius: 16,
  backgroundColor: 'rgba(249, 234, 234, 1)',
  paddingTop: 10,
  paddingBottom: 10,
  paddingRight: 20,
  display: 'flex',
  flexDirection: 'row',
  paddingLeft: 20,
  alignItems: 'center',
}
